import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Icon from 'src/components/ui/Icon/Icon';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import API_BASE_URL from 'src/constants';
import logger from 'src/utils/logger';
import {
  MAX_DOCUMENT_COUNT,
  MAX_DOCUMENT_SIZE_BYTES,
  REGISTRATION_DOCUMENT_TYPES,
  RegistrationDocument,
  RegistrationDocumentType,
} from './schema';
import { extractBackendErrorMessage, parseFetchError } from './errorUtils';

interface RegistrationDocumentUploaderProps {
  registrationKey: string;
  documentType: RegistrationDocumentType;
  documents: RegistrationDocument[];
  totalDocumentCount: number;
}

const uploadDocument = async (
  registrationKey: string,
  file: File,
  documentType: RegistrationDocumentType,
): Promise<RegistrationDocument> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);

  const response = await fetch(
    `${API_BASE_URL}portal/registrations/${encodeURIComponent(registrationKey)}/documents/`,
    {
      method: 'POST',
      body: formData,
    },
  );

  if (!response.ok) {
    throw await parseFetchError(response);
  }
  return response.json();
};

const deleteDocument = async (registrationKey: string, documentId: number): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}portal/registrations/${encodeURIComponent(registrationKey)}/documents/${documentId}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    throw await parseFetchError(response);
  }
};

const RegistrationDocumentUploader: React.FC<RegistrationDocumentUploaderProps> = ({
  registrationKey,
  documentType,
  documents,
  totalDocumentCount,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isLogo = documentType === REGISTRATION_DOCUMENT_TYPES.LOGO;
  const copyKeys: Record<RegistrationDocumentType, { title: string; description: string }> = {
    [REGISTRATION_DOCUMENT_TYPES.LOGO]: {
      title: 'registration.documents.logo_title',
      description: 'registration.documents.logo_description',
    },
    [REGISTRATION_DOCUMENT_TYPES.ID_DOCUMENT]: {
      title: 'registration.documents.id_title',
      description: 'registration.documents.id_description',
    },
    [REGISTRATION_DOCUMENT_TYPES.OTHER]: {
      title: 'registration.documents.other_title',
      description: 'registration.documents.other_description',
    },
  };
  const { title: titleKey, description: descriptionKey } = copyKeys[documentType];

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: ['get', '/registrations/{registration_key}/documents/'],
    });
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
      setError(t('registration.documents.too_large'));
      return;
    }
    if (!isLogo && totalDocumentCount >= MAX_DOCUMENT_COUNT) {
      setError(t('registration.documents.too_many', { max: MAX_DOCUMENT_COUNT }));
      return;
    }

    setError(null);
    setBusy(true);
    try {
      if (isLogo) {
        await Promise.all(documents.map(doc => deleteDocument(registrationKey, doc.id)));
      }
      await uploadDocument(registrationKey, file, documentType);
      invalidate();
    } catch (err) {
      logger.error('Document upload failed', err);
      const backendMessage = extractBackendErrorMessage(err);
      const fallback = t('registration.documents.upload_failed');
      setError(backendMessage || fallback);
      toast.error(backendMessage || fallback);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (docId: number) => {
    setError(null);
    setBusy(true);
    try {
      await deleteDocument(registrationKey, docId);
      invalidate();
    } catch (err) {
      logger.error('Document delete failed', err);
      const backendMessage = extractBackendErrorMessage(err);
      const fallback = t('registration.documents.delete_failed');
      setError(backendMessage || fallback);
      toast.error(backendMessage || fallback);
    } finally {
      setBusy(false);
    }
  };

  const canUpload = isLogo || totalDocumentCount < MAX_DOCUMENT_COUNT;

  return (
    <div className="border border-subtle rounded-8 p-16">
      <div className="flex items-start justify-between mb-12">
        <div>
          <h4 className="typo-title-20 text-full">{t(titleKey)}</h4>
          <p className="typo-text-14 text-faded mt-4">{t(descriptionKey)}</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={isLogo ? 'image/*' : 'image/*,application/pdf'}
          onChange={onFileSelected}
        />
        <Button
          title={
            isLogo && documents.length > 0 ? t('registration.documents.replace') : t('registration.documents.upload')
          }
          tone={ButtonToneProps.SECONDARY}
          icon="imageUpload"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy || !canUpload}
        />
      </div>

      {documents.length === 0 && (
        <p className="typo-text-14 text-faded italic">{t('registration.documents.none_yet')}</p>
      )}

      {documents.length > 0 && (
        <ul className="flex flex-col gap-8">
          {documents.map(doc => (
            <li
              key={doc.id}
              className="flex items-center justify-between gap-12 px-12 py-8 border border-subtle rounded-8">
              <div className="flex items-center gap-8 min-w-0">
                <Icon name="file" width={18} height={18} />
                <span className="typo-text-14 text-full truncate">{doc.document_name}</span>
              </div>
              <button
                type="button"
                onClick={() => onDelete(doc.id)}
                disabled={busy}
                className="status-error-full"
                aria-label={t('general.delete')}>
                <Icon name="delete" width={18} height={18} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="typo-text-12--bold status-error-full mt-8">{error}</p>}
    </div>
  );
};

export default RegistrationDocumentUploader;
